import {downloadFile} from '@domain/file-download'
import {useFundamentalsLanguage} from '@hconnect/common/hooks'
import {
  Alert,
  Button,
  ControlledDragAndDropFiles,
  Loading,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  TextRegular,
  TextSmallRegular
} from '@hm/fundamental'
import {mdiCheck, mdiClose, mdiDownload} from '@mdi/js'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {BulkUploadPlantActionsErrors} from '../components'
import {
  FEATURE_NAMESPACE,
  PLANT_ACTIONS_BULK_UPLOAD_ACCEPTED_EXTENSION,
  PLANT_ACTIONS_BULK_UPLOAD_TEMPLATE_FILE_NAME
} from '../consts'
import {useBulkUploadPlantActions} from '../hooks'
import {
  createPlantActionsBulkUploadTemplate,
  getBulkUploadFailure,
  getMandatoryBulkUploadColumnNames,
  isPlantActionsBulkUploadFile
} from '../utils'

type Props = {
  open: boolean
  onClose: () => void
}

const DATA_TEST_ID = 'bulk-upload-plant-actions-dialog'

export const BulkUploadPlantActionsDialog: React.FC<Props> = ({open, onClose}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const language = useFundamentalsLanguage()
  const [file, setFile] = useState<File>()
  const [fileError, setFileError] = useState<string>()
  const {mutate: bulkUpload, isPending, error, data, reset} = useBulkUploadPlantActions()

  const failure = error ? getBulkUploadFailure(error) : undefined
  // Only reachable for a header-only file, successful uploads with rows close the dialog
  const isEmptyFile = data?.totalRows === 0

  const handleFileChange = (files: (File | {id: string})[]) => {
    if (isPending) return
    reset()
    const nextFile = files.findLast((entry): entry is File => entry instanceof File)
    if (nextFile && !isPlantActionsBulkUploadFile(nextFile)) {
      setFile(undefined)
      setFileError(t('pomAction.bulkUpload.message.invalidFileType'))
      return
    }
    setFile(nextFile)
    setFileError(undefined)
  }

  const handleClose = () => {
    if (isPending) return
    reset()
    setFile(undefined)
    setFileError(undefined)
    onClose()
  }

  const handleImport = () => {
    if (!file) return
    bulkUpload(file, {
      onSuccess: ({createdCount}) => {
        if (createdCount) handleClose()
      }
    })
  }

  const handleDownloadTemplate = () => {
    downloadFile(
      createPlantActionsBulkUploadTemplate(),
      PLANT_ACTIONS_BULK_UPLOAD_TEMPLATE_FILE_NAME
    )
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      isExitEnabled={!isPending}
      width="wide"
      data-cy={DATA_TEST_ID}
    >
      <ModalTitle>{t('pomAction.bulkUpload.label.title')}</ModalTitle>
      <ModalContent>
        <div className="flex flex-col gap-4">
          <TextRegular>{t('pomAction.bulkUpload.label.description')}</TextRegular>

          <ControlledDragAndDropFiles
            name="plant-actions-bulk-upload-file"
            aria-label={t('pomAction.bulkUpload.label.title')}
            accept={PLANT_ACTIONS_BULK_UPLOAD_ACCEPTED_EXTENSION}
            multiple={false}
            value={file ? [file] : []}
            onChange={handleFileChange}
            hint={t('pomAction.bulkUpload.label.requiredColumns', {
              columns: getMandatoryBulkUploadColumnNames().join(', ')
            })}
            error={fileError}
            touched={Boolean(fileError)}
            disabled={isPending}
            language={language}
            data-cy={`${DATA_TEST_ID}-dropzone`}
          />

          <div>
            <Button
              type="button"
              variant="tertiary"
              size="small"
              icon={mdiDownload}
              iconPosition="left"
              onClick={handleDownloadTemplate}
              data-test-id={`${DATA_TEST_ID}-download-template`}
            >
              {t('pomAction.bulkUpload.action.downloadTemplate')}
            </Button>
          </div>

          {isEmptyFile && (
            <Alert variant="warning" data-cy={`${DATA_TEST_ID}-empty-file`}>
              <TextSmallRegular>{t('pomAction.bulkUpload.message.emptyFile')}</TextSmallRegular>
            </Alert>
          )}

          {failure && (
            <BulkUploadPlantActionsErrors
              failure={failure}
              data-test-id={`${DATA_TEST_ID}-error`}
            />
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button
          type="button"
          variant="tertiary"
          size="regular"
          icon={mdiClose}
          iconPosition="left"
          onClick={handleClose}
          disabled={isPending}
          data-test-id={`${DATA_TEST_ID}-cancel-button`}
        >
          {t('pomAction.bulkUpload.action.cancel')}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="regular"
          icon={isPending ? <Loading size="xsmall" variant="secondary" /> : mdiCheck}
          iconPosition="left"
          onClick={handleImport}
          disabled={!file || isPending}
          data-test-id={`${DATA_TEST_ID}-import-button`}
        >
          {t('pomAction.bulkUpload.action.import')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
